import {
  AssignmentOutlined,
  CancelOutlined,
  Check,
  CheckBox,
  CheckBoxOutlineBlank,
  CloudSyncOutlined,
  FilterDramaOutlined,
  NoteAddOutlined,
} from "@mui/icons-material";
import { lazy, Suspense, useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { isNil, set } from "lodash";
import LoadingFallback from "../../../components/LoadingFallback";
import ConfirmationDialog from "../../../components/ui/ConfirmationDialog";
import { useEnv } from "../../../hooks/useEnv";
import { useLoanTermsHook } from "../../../hooks/useLoanTermsHook";
import { useReduxHook } from "../../../hooks/useReduxHook";
import { useReduxLoanDrilldownHook } from "../../../hooks/useReduxLoanDrilldownHook";
import { useUnderwritingHook } from "../../../hooks/useUnderwritingHook";
import { loanDrilldownSet } from "../../../redux/actions/loanDrilldownActions";
import { sidebarReset } from "../../../redux/actions/sidebarActions";
import { Button } from "@mui/material";
import CheckboxInput from "../../../_src_shared/components/inputs/CheckboxInput";
import BasicModal from "../../../components/modals/BasicModal";
import RffForm from "../../../components/finalForm/RffForm";
import RffTextField from "../../../components/finalForm/inputs/RffTextField";
import RffTextareaField from "../../../components/finalForm/inputs/RffTextareaField";
import RffGroup from "../../../components/finalForm/shared/RffGroup";
import { green, red } from "@mui/material/colors";

// Lazy load heavy components
const DropdownButton = lazy(
  () => import("../../../components/ui/buttons/DropdownButton")
);
const LoanBulkFileUploader = lazy(
  () => import("../shared/LoanBulkFileUploader")
);

/**
 * Types: "Closed", "Dead"
 */
const LoanDrilldownButtonMarkLoan = ({ type }) => {
  const dispatch = useDispatch();
  const loanDrilldown = useSelector((state) => state.loanDrilldown);

  const [show, setShow] = useState(false);

  const isClosedOrDead = useMemo(() => {
    if (type === "Closed") {
      if (
        loanDrilldown?.loanStatus === "Closed" ||
        loanDrilldown?.loanStatus === "Closed w/ Post Closing Conditions"
      ) {
        return true;
      }
    }
    if (type === "Dead") {
      if (loanDrilldown?.loanStatus === "Dead") {
        return true;
      }
    }
    return false;
  }, [loanDrilldown, type]);

  const isDisabled = useMemo(() => {
    if (type === "Dead") {
      if (
        loanDrilldown?.loanStatus === "Closed" ||
        loanDrilldown?.loanStatus === "Closed w/ Post Closing Conditions"
      ) {
        return true;
      }
    }
    if (type === "Closed") {
      if (loanDrilldown?.loanStatus === "Dead") {
        return true;
      }
    }

    return false;
  }, [loanDrilldown?.loanStatus, type]);

  const color = useMemo(() => {
    if (type === "Closed") {
      return "success";
    }
    if (type === "Dead") {
      return "error";
    }
    if (isClosedOrDead) {
      if (type === "Closed") {
        return "success";
      }
      if (type === "Dead") {
        return "error";
      }
    }

    return "primary";
  }, [isClosedOrDead, type]);

  const icon = useMemo(() => {
    if (isClosedOrDead) {
      if (type === "Closed") {
        return <CheckBox fontSize="small" />;
      }
      if (type === "Dead") {
        return <CancelOutlined fontSize="small" />;
      }
    }

    return <CheckBoxOutlineBlank fontSize="small" />;
  }, [isClosedOrDead, type]);

  const buttonText = useMemo(() => {
    if (type === "Closed") {
      return isClosedOrDead ? "Loan Marked As Closed" : "Mark Loan Closed";
    }
    if (type === "Dead") {
      return isClosedOrDead ? "Loan Marked As Dead" : "Mark Loan Dead";
    }
  }, [type, isClosedOrDead]);

  const styles = useMemo(() => {
    if (isClosedOrDead) {
      if (type === "Closed") {
        return {
          background: green[50],
        };
      }
      if (type === "Dead") {
        return {
          background: red[50],
        };
      }
    }
  }, [type, isClosedOrDead]);

  const onClick = useCallback(() => {
    if (isClosedOrDead) {
      return;
    }
    setShow(true);
  }, [isClosedOrDead]);

  if (type !== "Closed" && type !== "Dead") {
    return null;
  }
  // Only show if admin
  return (
    <Suspense fallback={<LoadingFallback />}>
      {show && type === "Closed" && (
        <CloseLoanConfirmationModal onClose={() => setShow(false)} />
      )}
      {show && type === "Dead" && (
        <DeadLoanConfirmationModal onClose={() => setShow(false)} />
      )}

      {/* <CheckboxInput
        label={buttonText}
        size="small"
        // size="large"
        onClick={() => setShow(true)}
        startIcon={<CheckBoxOutlineBlank fontSize="small" />}
        variant="outlined"
      /> */}

      <Button
        // size="medium"
        // size="large"
        sx={styles}
        disabled={isDisabled}
        color={color}
        onClick={onClick}
        startIcon={icon}
        // variant={"text"}
        variant={isClosedOrDead ? "text" : "contained"}
      >
        {buttonText}
      </Button>
    </Suspense>
  );
};

const CloseLoanConfirmationModal = ({ type, onClose }) => {
  const loanDrilldown = useSelector((state) => state.loanDrilldown);

  const { loading, markDealClosed, markDealDead, getZillowZipcodeData } =
    useUnderwritingHook();

  const dispatch = useDispatch();

  const onSubmit = () => {
    markDealClosed({
      dealId: loanDrilldown?._id,
      onSuccessFn: (data) => {
        dispatch(loanDrilldownSet(data));
        onClose();
      },
    });
  };

  return (
    <ConfirmationDialog
      open
      maxWidth="xs"
      loading={loading}
      onCancel={onClose}
      onConfirm={onSubmit}
      title={`Mark Loan Closed`}
      message={"Are you sure you want to mark this loan as Closed?"}
      confirmText={`Yes, Mark as Closed`}
    />
  );
};

const DeadLoanConfirmationModal = ({ onClose }) => {
  const loanDrilldown = useSelector((state) => state.loanDrilldown);

  const { loading, markDealDead, getZillowZipcodeData } = useUnderwritingHook();

  const dispatch = useDispatch();

  const onSubmit = (values) => {
    console.log("values", values);
    // return;
    markDealDead({
      dealId: loanDrilldown?._id,
      payload: values,
      onSuccessFn: (data) => {
        dispatch(loanDrilldownSet(data));
        onClose();
      },
    });
  };

  return (
    <BasicModal
      show
      maxWidth="xs"
      loading={loading}
      onClose={onClose}
      onConfirm={onSubmit}
      title={`Mark Loan Dead`}
      message={"Are you sure you want to mark this loan as Closed?"}
      confirmText={`Yes, Mark as Closed`}
    >
      <RffForm loading={loading} onSubmit={onSubmit}>
        <RffGroup>
          <RffTextField name="dealDeadReason" label="Reason" />
          <RffTextareaField
            name="dealDeadNotes"
            label="Related Notes"
            minHeight={80}
          />
        </RffGroup>
      </RffForm>
    </BasicModal>
  );
};
const DeadLoanConfisdarmationModal = ({ type, onClose }) => {
  const loanDrilldown = useSelector((state) => state.loanDrilldown);

  const { loading, markDealClosed, markDealDead, getZillowZipcodeData } =
    useUnderwritingHook();

  const dispatch = useDispatch();

  const onSubmit = () => {
    if (type === "Closed") {
      // getZillowZipcodeData({
      //   dealId: loanDrilldown?._id,
      //   onSuccessFn: (data) => {
      //     // dispatch(loanDrilldownSet(data));
      //     onClose();
      //   },
      // });
      markDealClosed({
        dealId: loanDrilldown?._id,
        onSuccessFn: (data) => {
          dispatch(loanDrilldownSet(data));
          onClose();
        },
      });
    }
    if (type === "Dead") {
      markDealDead({
        dealId: loanDrilldown?._id,
        onSuccessFn: (data) => {
          dispatch(loanDrilldownSet(data));
          onClose();
        },
      });
    }
  };

  const buttonText = useMemo(() => {
    if (type === "Closed") {
      return "Are you sure you want to mark this loan as Closed?";
    }
    if (type === "Dead") {
      return "Are you sure you want to mark this loan as Deal?";
    }
  }, [type]);

  return (
    <ConfirmationDialog
      open
      maxWidth="xs"
      loading={loading}
      onCancel={onClose}
      onConfirm={onSubmit}
      title={`Mark Loan ${type}`}
      message={buttonText}
      confirmText={`Yes, Mark as ${type}`}
    />
  );
};

export default LoanDrilldownButtonMarkLoan;
